import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultHttpClient, HttpRequest, HttpResponse, HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@aspnet/signalr';
import { AuthService } from '@pg-app/core/services/auth.service';
import { environment } from '@pg-env/environment';
import { Observable, Subject } from 'rxjs';

export interface IPricingData {
  status: string;
  spotRate: Number;
  exchangeFee: Number;
  clientRate: Number;
  sellAmount: Number;
  buyAmount: Number;
  type: string;
  quoteId: string;
}

export interface IExchangeFeeDto {
  exchangeFee: Number;
  quoteId: string;
}

export interface IClientRateDto {
  exchangeFee: Number;
  quoteId: string;
  clientRate: string;
}

class CustomHttpClient extends DefaultHttpClient {
  constructor() {
    super(console); 
  }
  public send(request: HttpRequest): Promise<HttpResponse> {
    request.headers = { ...request.headers, "TenantId": environment.tenantId };
    return super.send(request);
  }
}

@Injectable({
  providedIn: 'root'
})
export class PricingService {

  private _hubConnection: HubConnection;
  private pricingSignalRUrl = environment.pricingSignalRUrl;
  private pricingApiUrl = environment.pricingApiUrl;
  public connectionExists = false;
  public pricingEvent = new Subject<any>();

  constructor(private auth: AuthService, private http: HttpClient) { }

  public startConnection(callback = null): void {
    this._hubConnection = new HubConnectionBuilder().withUrl(`${this.pricingSignalRUrl}?TenantId=${environment.tenantId}`
      , {
        httpClient: new CustomHttpClient(),
        accessTokenFactory: () => this.auth.accessToken
      })
      .configureLogging(LogLevel.Information)
      .build();

    this._hubConnection.start().then(() => {
      this.connectionExists = true;

      if (callback && typeof callback === 'function') {
        callback();
      }
    }).catch((err: any) => {
      this.connectionExists = false;
    });
  }

  public registerPricingService(
    tradeTypeId: string,
    isForwardTrade: boolean,
    currencyTypeSellId: string,
    sellCode: string,
    sellAmount: Number,
    currencyTypeBuyId: string,
    buyCode: string,
    isSellChanged: boolean,
    buyAmount: Number,
    valueDate: string,
    exchangeFee: Number,
    quoteId: string
  ) {
    if (!this.connectionExists) {
      this.startConnection(() => {
        this.registerPricingService(
          tradeTypeId,
          isForwardTrade,
          currencyTypeSellId,
          sellCode,
          sellAmount,
          currencyTypeBuyId,
          buyCode,
          isSellChanged,
          buyAmount,
          valueDate,
          exchangeFee, 
          quoteId
        );
      });
      return;
    }

    this.registerListeners();

    this._hubConnection.invoke('RegisterPricingService',
      tradeTypeId,
      isForwardTrade,
      currencyTypeSellId,
      sellCode,
      sellAmount,
      currencyTypeBuyId,
      buyCode,
      buyAmount,
      isSellChanged,
      valueDate,
      exchangeFee,
      quoteId)
      .then((data: any) => {
      })
      .catch((err) => {
      });
  }

  public registerListeners() {
    if (!this._hubConnection) {
      return;
    }

    this._hubConnection.on('UpdateBuy', (pricingData: IPricingData) => {
      pricingData.type = 'UpdateBuy';
      this.pricingEvent.next(pricingData);
    });


    this._hubConnection.on('UpdateSell', (pricingData: IPricingData) => {
      pricingData.type = 'UpdateSell';
      this.pricingEvent.next(pricingData);
    });
  }

  public offListeners() {
    if (!this._hubConnection) {
      return;
    }

    const eventNames = ['UpdateBuy', 'UpdateSell'];
    eventNames.forEach(eventName => {
      this._hubConnection.off(eventName);
    });
  }

  public stopConnection(callback = null) {
    if (!this._hubConnection) {
      if (callback && typeof callback === 'function') {
        callback();
      }
      return;
    }

    this._hubConnection.stop().then(() => {
      this.connectionExists = false;
      if (callback && typeof callback === 'function') {
        callback();
      }
    }).catch((err) => {
    });
  }

  public getClientRate(sellCurrencyCode: string,
    buyCurrencyCode: string,
    exchangeFee: Number,
    valueDateString: string,
    quoteId: string,
    userId: string = ''
  ): Observable<IClientRateDto> {
    const api = `${this.pricingApiUrl}/pricing/get-clientRate?sellCurrencyCode=${
      sellCurrencyCode}&buyCurrencyCode=${
      buyCurrencyCode}&exchangeFee=${
      exchangeFee}&userId=${
      userId}&quoteId=${
      quoteId}&valueDate=${valueDateString}`;

    return this.http.get<IClientRateDto>(api);
  }

  public getClientForwardRate(
    sellCurrencyCode: string,
    buyCurrencyCode: string,
    exchangeFee: Number,
    valueDateString: string,
    quoteId: string,
    userId: string = ''
  ): Observable<IClientRateDto> {
    const api = `${this.pricingApiUrl}/pricing/get-client-forwardRate?sellCurrencyCode=${
      sellCurrencyCode}&buyCurrencyCode=${
      buyCurrencyCode}&exchangeFee=${
      exchangeFee}&userId=${
      userId}&valueDate=${
      valueDateString}&quoteId=${quoteId}`;

    return this.http.get<IClientRateDto>(api, {
      headers: {
        useLocalHandleException: 'Y'
      }
    });
  }

  public getExchangeFee(tradeTypeId: string,
    currencyTypeSellId: string,
    currencyTypeBuyId: string,
    sellCode: string,
    buyCode: string,
    isSellChange: boolean,
    amount: Number,
    userId: string,
    isForwardTrade: boolean, 
    valueDate: string): Observable<IExchangeFeeDto> {
    const api = `${this.pricingApiUrl}/pricing/get-exchangeFee?tradeTypeId=${
      tradeTypeId}&currencyTypeSellId=${
      currencyTypeSellId}&currencyTypeBuyId=${
      currencyTypeBuyId}&sellCode=${
      sellCode}&buyCode=${
      buyCode}&isSellChange=${
      isSellChange}&amount=${
      amount}&valueDate=${
      valueDate}&isForwardTrade=${
      isForwardTrade}&userId=${
      userId}&isGetQuote=${
        true}`;
    return this.http.get<IExchangeFeeDto>(api);
  }

  public updateByClientRate(
    buyCurrencyCode: string,
    buyAmount: Number,
    sellCurrencyCode: string,
    sellAmount: Number,
    clientRate: Number,
    isSellChanged: boolean,
    quoteId: string = null): Observable<IPricingData> {

    const api = `${this.pricingApiUrl}/pricing/update-by-client-rate?sellCurrencyCode=${sellCurrencyCode
      }&buyCurrencyCode=${buyCurrencyCode
      }&sellAmount=${sellAmount
      }&buyAmount=${buyAmount
      }&clientRate=${clientRate
      }&isSellChanged=${isSellChanged}&quoteId=${quoteId}`;

    return this.http.get<IPricingData>(api);
  }

  public updateByClientRateNoQuoteId(
    buyCurrencyCode: string,
    buyAmount: Number,
    sellCurrencyCode: string,
    sellAmount: Number,
    clientRate: Number,
    isSellChanged: boolean): Observable<IPricingData> {

    const api = `${this.pricingApiUrl}/pricing/update-by-client-rate?sellCurrencyCode=${sellCurrencyCode
      }&buyCurrencyCode=${buyCurrencyCode
      }&sellAmount=${sellAmount
      }&buyAmount=${buyAmount
      }&clientRate=${clientRate
      }&isSellChanged=${isSellChanged}`;

    return this.http.get<IPricingData>(api);
  }
}
